/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// eslint-disable-next-line no-unused-vars
import styles from "./DeleteEntryModal.module.css";
import { useHotkey } from "../../../Utils/Hotkeys";
import BaseModal, { useBaseModal } from "../BaseModal/BaseModal";
import ModalCloseButton from "../ModalCloseButton/ModalCloseButton";

const propTypes = {
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const defaultProps = {
  onOpen: () => {},
  onClose: () => {},
  loading: false,
};

const useDeleteEntryModal = ({ onOpen, onClose, onDeleteEntry, loading }) => {
  const { isOpened, open, close } = useBaseModal({ onOpen, onClose });
  const [entry, setEntry] = useState(null);

  let content = null;
  if (isOpened) {
    content = (
      <DeleteEntryModal
        show={isOpened}
        onCloseHandler={close}
        entry={entry}
        onDeleteHandler={onDeleteEntry}
        deleteLoading={loading}
      />
    );
  }

  const openModal = (entryToDelete) => {
    setEntry(entryToDelete);
    open();
  };

  const closeModal = () => {
    close();
    setEntry(null);
  };

  return { open: openModal, close: closeModal, content };
};

useDeleteEntryModal.propTypes = propTypes;
useDeleteEntryModal.defaultProps = defaultProps;

const propTypesModal = {
  show: PropTypes.bool.isRequired,
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  deleteLoading: PropTypes.bool.isRequired,
  onDeleteHandler: PropTypes.func.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
};

const DeleteEntryModal = ({
  show,
  entry,
  deleteLoading,
  onDeleteHandler,
  onCloseHandler,
}) => {
  const { t } = useTranslation();
  const checkBox = useRef(null);
  const text = t("wisdomtree.modal_delete_entry.text", {
    entry_name: entry.name,
  });

  useHotkey({
    keyNames: ["Enter"],
    callback: () => {
      if (!show) return;
      onDeleteHandler(entry, checkBox.current.checked);
    },
    dependsOn: [onDeleteHandler, entry, checkBox, show],
  });

  useHotkey({
    keyNames: ["Escape"],
    callback: () => {
      if (!show) return;
      onCloseHandler();
    },
    dependsOn: [onCloseHandler, show],
  });

  return (
    <BaseModal show={show} onCloseHandler={onCloseHandler}>
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">
            {t("wisdomtree.modal_delete_entry.title")}
          </p>
          <ModalCloseButton onClick={onCloseHandler} disabled={deleteLoading} />
        </header>
        <section className="modal-card-body">
          <p>{text}</p>
          <label className="checkbox">
            <input type="checkbox" ref={checkBox} />
            {t("wisdomtree.modal_delete_entry.include_children")}
          </label>
        </section>
        <footer className="modal-card-foot">
          <button
            className={`button is-danger ${deleteLoading ? `is-loading` : ``}`}
            type="submit"
            onClick={() => {
              onDeleteHandler(entry, checkBox.current.checked);
            }}
          >
            {t("wisdomtree.modal_delete_entry.delete")}
          </button>
          <button
            className="button"
            type="button"
            onClick={onCloseHandler}
            disabled={deleteLoading}
          >
            {t("wisdomtree.modal_delete_entry.cancel")}
          </button>
        </footer>
      </div>
    </BaseModal>
  );
};

DeleteEntryModal.propTypes = propTypesModal;

export default useDeleteEntryModal;
